import clsx from 'clsx';

import { Checkbox } from '@/components/ui/checkbox';
import { HighlightText } from '@/components/ui/highlight-text';

interface EmailCardProps {
  id: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  searchQuery?: string;
  sender: string;
  senderColor: string;
  senderInitial: string;
  showCheckboxes?: boolean;
  snippet?: string;
  subject: string;
  time: string;
}

export function EmailCard({
  isDisabled = false,
  isSelected = false,
  onClick,
  searchQuery = '',
  sender,
  showCheckboxes = false,
  snippet,
  subject,
  time,
}: EmailCardProps) {
  const shouldShowCheckbox = isSelected || showCheckboxes;
  const hasSearchQuery = !!searchQuery?.trim();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  const renderHighlightedContent = () => {
    const searchLower = searchQuery?.toLowerCase().trim() ?? '';
    const senderLower = sender.toLowerCase();
    const subjectLower = subject.toLowerCase();

    const matchesNameOrSubject =
      hasSearchQuery &&
      !snippet && // No body snippet means match is in name/subject
      (senderLower.includes(searchLower) || subjectLower.includes(searchLower));

    const subjectText = `Sub: ${subject}`;

    const textItems = [
      {
        className:
          "mt-0.5 text-[12px] font-medium text-tertiary truncate leading-[1.5] tracking-normal font-['Inter']",
        text: subjectText,
      },
    ];

    if (snippet && hasSearchQuery) {
      textItems.push({
        className: "mt-1 text-[11px] text-neutral-mid leading-[1.4] line-clamp-2 font-['Inter']",
        text: snippet,
      });
    } else if (matchesNameOrSubject) {
      textItems.push({
        className: "mt-1 text-[11px] text-neutral-mid leading-[1.4] font-['Inter']",
        text: `From: ${sender}`,
      });
    }

    return textItems.map((item, index) => (
      <p className={item.className} key={index}>
        <HighlightText searchQuery={searchQuery} text={item.text} />
      </p>
    ));
  };

  return (
    <div
      className={clsx(
        'group w-full rounded-md px-2.5 py-1.5 text-left transition-colors duration-200 ease-out focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:outline-none',
        isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        isSelected ? 'bg-elevated-subtle' : 'bg-back-bg2',
      )}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
    >
      <div className="flex flex-col">
        {/* Row 1: Avatar + Sender + Time */}
        <div className="flex items-center gap-2.5">
          {/* Avatar / Checkbox */}
          <div className="relative h-[22px] w-[22px] flex-shrink-0">
            {/* Checkbox */}
            <div
              className={clsx(
                'absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out',
                shouldShowCheckbox ? 'opacity-100' : 'opacity-0',
                !shouldShowCheckbox && !isDisabled && 'group-hover:opacity-100',
              )}
              onClick={(e) => e.stopPropagation()} // prevent email open
            >
              <Checkbox checked={isSelected} onCheckedChange={() => onClick?.()} />
            </div>
          </div>

          {/* Sender + time */}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <span className="text-foreground truncate font-['Inter'] text-[12px] leading-[1.5] font-semibold tracking-normal">
              <HighlightText searchQuery={searchQuery} text={sender} />
            </span>
            <span className="text-neutral-mid flex-shrink-0 text-[10.5px]">{time}</span>
          </div>
        </div>

        {/* Subject and Snippet with combined highlighting logic */}
        {renderHighlightedContent()}
      </div>
    </div>
  );
}
